/**
 * Upload-time image compression (client-side, on the uploader's device).
 *
 * Why: sellers upload uncompressed camera PNGs (2-3MB+). We store them
 * full-size, so every cache-miss in the Next.js image optimizer pays a
 * ~1.5s sharp transcode of the original, and raw <img> consumers ship
 * megabytes to the browser. Compressing once at upload kills the cost at
 * the root instead of at every display site.
 *
 * Split: `decideCompression` is a pure decision (unit-tested,
 * tests/unit/image-compress-decision.test.ts); `compressImageFile` is the
 * browser adapter (canvas encode, fail-open to the original file).
 *
 * @see .tondocs/images/IMAGES_ARCHITECTURE.md
 * @see scripts/backfill-compress-images.ts — server-side twin for existing objects
 */

export const COMPRESS_MAX_DIMENSION = 2048;
export const COMPRESS_QUALITY = 0.82;
/** At or below this size (and within max dimensions) re-encoding buys little. */
export const COMPRESS_SKIP_BYTES = 400 * 1024;

/** gif (animation) and svg (vector) must never go through a canvas re-encode. */
const COMPRESSIBLE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/bmp",
]);

export type CompressionDecision =
  | { kind: "skip"; reason: "unsupported-type" | "already-small" }
  | { kind: "compress"; maxDimension: number; quality: number };

export function decideCompression(input: {
  mimeType: string;
  bytes: number;
  width: number;
  height: number;
}): CompressionDecision {
  if (!COMPRESSIBLE_MIME_TYPES.has(input.mimeType)) {
    return { kind: "skip", reason: "unsupported-type" };
  }
  const oversized = Math.max(input.width, input.height) > COMPRESS_MAX_DIMENSION;
  if (!oversized && input.bytes <= COMPRESS_SKIP_BYTES) {
    return { kind: "skip", reason: "already-small" };
  }
  return {
    kind: "compress",
    maxDimension: COMPRESS_MAX_DIMENSION,
    quality: COMPRESS_QUALITY,
  };
}

let webpEncodeSupport: boolean | null = null;

/** Safari <17 cannot encode webp via canvas — falls back to jpeg there. */
function canEncodeWebp(): boolean {
  if (webpEncodeSupport === null) {
    try {
      webpEncodeSupport = document
        .createElement("canvas")
        .toDataURL("image/webp")
        .startsWith("data:image/webp");
    } catch {
      webpEncodeSupport = false;
    }
  }
  return webpEncodeSupport;
}

/**
 * Downscale to COMPRESS_MAX_DIMENSION and re-encode as webp (jpeg fallback).
 * Fail-open: any decode/encode problem, or a result that isn't smaller,
 * returns the original file untouched — upload must never be blocked by
 * compression.
 */
export async function compressImageFile(file: File): Promise<File> {
  if (typeof window === "undefined") return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return file;
  }

  try {
    const decision = decideCompression({
      mimeType: file.type,
      bytes: file.size,
      width: bitmap.width,
      height: bitmap.height,
    });
    if (decision.kind === "skip") return file;

    const scale = Math.min(1, decision.maxDimension / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;

    const targetType = canEncodeWebp() ? "image/webp" : "image/jpeg";
    if (targetType === "image/jpeg") {
      // jpeg has no alpha channel — flatten transparency onto white instead of black
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
    }
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, targetType, decision.quality)
    );
    if (!blob || blob.size >= file.size) return file;

    const extension = targetType === "image/webp" ? "webp" : "jpg";
    return new File([blob], replaceFileExtension(file.name, extension), {
      type: targetType,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  } finally {
    bitmap.close();
  }
}

function replaceFileExtension(filename: string, newExtension: string): string {
  const base = filename.includes(".") ? filename.slice(0, filename.lastIndexOf(".")) : filename;
  return `${base}.${newExtension}`;
}
